// Module Mapping Configuration for Cognitive Assessment Integration
// Maps existing sequential modules to cognitive assessment system

import { ScoredModule1_1 } from './module1-1';
import AdaptiveModule2 from './module2-adaptive';
import AdaptiveModule3 from './module3-adaptive';
import AdaptiveModule3Original from './module3-3/adaptive-wrapper';
import AdaptiveModule6 from './module6-adaptive';
import AdaptiveModule7 from './module7-adaptive';

// Import remaining original modules (to be updated)
import Module1_0 from "./module1-0";
import Module1_2, { ScoredModule1_2 } from "./module1-2";
import Module1_3 from "./module1-3";
import Module1_4, { ScoredModule1_4 } from "./module1-4";
import Module1_5 from "./module1-5";
import Module2_1 from "./module2-1";
import Module2_2 from "./module2-2";
import Module2_3 from "./module2-3";
import Module2_4 from "./module2-4";
import Module3_1 from "./module3-1";
// import Module3_2 from "./module3-2"; // REMOVED - moved to module2-3 (Final Orders)
import Module3_3 from "./module3-3";
import Module3_4 from "./module3-4";
import Module4_1 from "./module4-1";
import Module4_2, { ScoredModule4_2 } from "./module4-2";
import Module4_3 from "./module4-3";
import Module4_4, { ScoredModule4_4 } from "./module4-4";
import Module4_5 from "./module4-5";
import Module4_6, { ScoredModule4_6 } from "./module4-6";
import Module4_7 from "./module4-7";
import Module5_1 from "./module5-1";
import Module5_2, { ScoredModule5_2 } from "./module5-2";
import Module5_3 from "./module5-3";
import Module6_1 from "./module6-1";
import Module6_2 from "./module6-2";
import Module6_3 from "./module6-3";
import Module7_1 from "./module7-1";
import Module7_2 from "./module7-2";
import Module7_3 from "./module7-3";
import Module8_1 from "./module8-1";
import Module8_2 from "./module8-2";
import Module8_3 from "./module8-3";
import Module8_4 from "./module8-4";
import Module8_5 from "./module8-5";
import Module8_6 from "./module8-6";
import Module8_7 from "./module8-7";
import Module8_8 from "./module8-8";

// Assessment mode flag - always use cognitive scoring
export const USE_COGNITIVE_SCORING = true;

// Mapping of your existing sequential modules to cognitive assessment modules
export const MODULE_MAPPING = {
  // Module 1A: Reading Emotions (F1, F3, F4) - Non-adaptive, difficulty 2
  '1A': {
    originalModules: [-1, 0], // Module1_0 (directions) and Module1_1 (emotion recognition)
    cognitiveType: '1A',
    isAdaptive: false,
    difficulty: 2,
    skills: ['F1', 'F3', 'F4'],
    description: 'Reading Emotions',
    component: USE_COGNITIVE_SCORING ? ScoredModule1_1 : null
  },

  // Module 1B: Food Preferences - Non-adaptive, difficulty 2
  '1B': {
    originalModules: [2, 3], // Module1_3 (directions), Module1_4 (drag-and-drop food preferences)
    cognitiveType: '1B',
    isAdaptive: false,
    difficulty: 2,
    skills: ['F1', 'F2e', 'F3'],
    description: 'Food Preferences - Drag & Drop', 
    component: USE_COGNITIVE_SCORING ? ScoredModule1_4 : null
  },

  // Module 2: Seating Guests (M1, M3, M2, M4, WM) - Adaptive, start difficulty 4
  '2': {
    originalModules: [5, 6], // 5=Module2_1 (directions), 6=AdaptiveModule2 (excludes progress page at 8)
    cognitiveType: '2',
    isAdaptive: true,
    startDifficulty: 4,
    instances: 5,
    skills: ['M1', 'M3', 'M2', 'M4', 'WM'],
    description: 'Seating Guests - Abstract Reasoning',
    component: USE_COGNITIVE_SCORING ? AdaptiveModule2 : null
  },

  // Module 3: Order Entry (M3, M2, M4) - Adaptive, start difficulty 4
  '3': {
    originalModules: [9, 12], // Module 3 pages: 9=Module3_1 directions, 12=AdaptiveModule3 (navigation jumps 9→12)
    cognitiveType: '3',
    isAdaptive: true,
    startDifficulty: 4,
    instances: 5,
    skills: ['M3', 'M2', 'M4'],
    description: 'Order Entry - Proprioceptive Memory',
    component: USE_COGNITIVE_SCORING ? AdaptiveModule3Original : null
  },

  // Module 4: Memory Tasks (M4, F2e, F4), (F2v, F2a), (M1, F2v, M4) - Non-adaptive, difficulty 2
  '4A': {
    originalModules: [14, 15], // 14=Module4_1 (directions), 15=ScoredModule4_2 (cognitive component)
    cognitiveType: '4A',
    isAdaptive: false,
    difficulty: 2,
    skills: ['M4', 'F2e', 'F4'],
    description: 'Memory Tasks A',
    component: USE_COGNITIVE_SCORING ? ScoredModule4_2 : null
  },
  '4B': {
    originalModules: [16, 17], // 16=Module4_3 (directions/transition), 17=ScoredModule4_4 (name recognition)
    cognitiveType: '4B',
    isAdaptive: false,
    difficulty: 2,
    skills: ['F2a', 'F1'],
    description: 'Memory Tasks B',
    component: USE_COGNITIVE_SCORING ? ScoredModule4_4 : null
  },
  '4C': {
    originalModules: [18, 19], // 18=Module4_5 (directions), 19=ScoredModule4_6 (face-name matching)
    cognitiveType: '4C',
    isAdaptive: false,
    difficulty: 2,
    skills: ['M1', 'F2v', 'M4'],
    description: 'Memory Tasks C',
    component: USE_COGNITIVE_SCORING ? ScoredModule4_6 : null
  },

  // Module 5: Logical Reasoning (F3, F2e, M2) - Non-adaptive, difficulty 1
  '5': {
    originalModules: [21, 22], // 21=Module5_1 (directions), 22=ScoredModule5_2 (Stroop task)
    cognitiveType: '5',
    isAdaptive: false,
    difficulty: 1,
    skills: ['F3', 'F2e', 'M2'],
    description: 'Logical Reasoning',
    component: USE_COGNITIVE_SCORING ? ScoredModule5_2 : null
  },

  // Module 6: Memory & Reasoning (M1, F2a, M3) - Adaptive, start difficulty 4
  '6': {
    originalModules: [24, 25], // 24=Module6_1 (directions), 25=AdaptiveModule6
    cognitiveType: '6',
    isAdaptive: true,
    startDifficulty: 5,
    instances: 5,
    skills: ['M1', 'F2a', 'M3'],
    description: 'Memory & Reasoning',
    component: USE_COGNITIVE_SCORING ? AdaptiveModule6 : null
  },

  // Module 7: Problem Solving (F3, F4, M2) - Adaptive, start difficulty 5
  '7': {
    originalModules: [27, 28], // 27=Module7_1 (directions), 28=AdaptiveModule7
    cognitiveType: '7',
    isAdaptive: true,
    startDifficulty: 5,
    instances: 5,
    skills: ['F3', 'F4', 'M2'],
    description: 'Problem Solving',
    component: USE_COGNITIVE_SCORING ? AdaptiveModule7 : null
  },

  // Module 8: Working Memory (WM, F2v) - Non-adaptive, difficulty 2
  '8': {
    originalModules: [30, 31, 32, 33, 34, 35, 36, 37], // Module8_1 through Module8_8
    cognitiveType: '8',
    isAdaptive: false,
    difficulty: 2,
    skills: ['WM', 'F2v'],
    description: 'Working Memory'
  }
};

// Helper function to get cognitive module type from sequential counter
export const getCognitiveModuleFromCounter = (cnt) => {
  for (const [cognitiveType, config] of Object.entries(MODULE_MAPPING)) {
    if (config.originalModules.includes(cnt)) {
      return {
        cognitiveType,
        config,
        isFirstInModule: config.originalModules.indexOf(cnt) === 0,
        isLastInModule: config.originalModules.indexOf(cnt) === config.originalModules.length - 1,
        moduleProgress: config.originalModules.indexOf(cnt) + 1,
        totalInModule: config.originalModules.length
      };
    }
  }
  return null;
};

// Helper function to get next cognitive module
export const getNextCognitiveModule = (currentCognitiveType) => {
  const types = Object.keys(MODULE_MAPPING);
  const currentIndex = types.indexOf(currentCognitiveType);
  if (currentIndex !== -1 && currentIndex < types.length - 1) {
    return types[currentIndex + 1];
  }
  return null;
};

// Configuration for fallback to original modules
export const createFallbackModuleConfig = (state, actions) => ({
  [-1]: {
    component: Module1_0,
    props: {
      setData: actions.handleSetData,
      nextPage: actions.navigateToNext
    }
  },
  [0]: {
    component: ScoredModule1_1,
    props: {
      nextPage: actions.navigateToNext
    }
  },
  [1]: {
    component: Module1_2,
    props: {
      nextPage: actions.navigateToNext,
      res: state.responses,
      cardData: state.cardData,
      Choose: actions.handleChoose
    }
  },
  [2]: {
    component: Module1_3,
    props: {
      nextPage: () => {
        actions.resetResponses();
        actions.navigateToNext();
      },
      res: state.responses,
      Choose: actions.handleChoose,
      emotionalIdx: state.emotionalIdx,
      setEmotionalIdx: actions.setEmotionalIdx,
      emotionalData: state.emotionalData,
      SelectDish: actions.handleSelectDish
    }
  },
  [3]: {
    component: Module1_4,
    props: {
      nextPage: () => {
        actions.resetSeat();
        actions.navigateToNext();
      },
      cardData: state.cardData,
      handleDrop: actions.handleDrop,
      imgIndex: state.imgIndex,
      flag: state.flag
    }
  },
  [4]: {
    component: Module1_5,
    props: {
      nextPage: actions.navigateToNext,
      progress: 1
    }
  },
  [5]: {
    component: Module2_1,
    props: {
      nextPage: actions.navigateToNext
    }
  },
  [6]: {
    component: Module2_2,
    props: {
      nextPage: actions.navigateToNext,
      cardData: state.cardData
    }
  },
  [7]: {
    component: Module2_3,
    props: {
      nextPage: actions.navigateToNext,
      cardData: state.cardData,
      resetSeat: actions.resetSeat,
      handleDrop: actions.handleDrop,
      imgIndex: state.imgIndex,
      flag: state.flag
    }
  },
  [8]: {
    component: Module2_4,
    props: {
      nextPage: actions.navigateToNext,
      progress: 2
    }
  },
  // ... continue with all other modules
});

export default MODULE_MAPPING;